// src/utils/froggerWrites.js
//
// A DOCK WRITE REFRESHES THE OPEN PAGE (made foods, Cowork QA 2026-10-21).
//
// A FroggerAI turn that wrote — a culture recorded, a task completed, an
// Undo that landed — left the Foods tab and Tasks Today showing the numbers
// they loaded with until a reload. The chat panel now says so on the window,
// ONE event, `froggerai:wrote` (`detail: {tools}` — the tools the turn
// called), whenever a turn's chunks show a write may have happened: a
// `readback` (`nodes.readback` emits one for a turn that called a write
// tool — usually one that wrote, but possibly one that wrote nothing, a
// refusal or a question; the refetch that follows is then a harmless
// re-read) with a sentence or undo tokens, or a successful `undo` tool
// result. A page that shows what a write can change listens with
// `useFroggerWrites` and reads again.
//
// The listener is DEBOUNCED (an undo turn can say it twice: the tool result
// and its receipt) and unmount-safe (a timer pending at unmount never fires
// into a dead component).
import { useEffect, useRef } from 'react';

export const WROTE_EVENT = 'froggerai:wrote';

/** Tell the open page a dock turn wrote. Never throws. */
export const announceWrite = (detail = {}) => {
  try {
    window.dispatchEvent(new CustomEvent(WROTE_EVENT, { detail }));
  } catch (err) {
    // A page with no CustomEvent (never in a supported browser) just
    // keeps its numbers until the next load.
  }
};

/** The quiet window: several announcements inside it are one refetch. */
export const WRITE_DEBOUNCE_MS = 400;

/**
 * Call `onWrite(detail)` once a dock write has settled — debounced, and
 * never after the component unmounts.
 * @param {Function} onWrite
 * @param {number} [delay]
 */
export function useFroggerWrites(onWrite, delay = WRITE_DEBOUNCE_MS) {
  const callback = useRef(onWrite);
  callback.current = onWrite;
  useEffect(() => {
    let timer = null;
    let alive = true;
    const onEvent = (event) => {
      if (timer) clearTimeout(timer);
      const detail = (event && event.detail) || {};
      timer = setTimeout(() => {
        timer = null;
        if (alive && typeof callback.current === 'function') callback.current(detail);
      }, delay);
    };
    window.addEventListener(WROTE_EVENT, onEvent);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      window.removeEventListener(WROTE_EVENT, onEvent);
    };
  }, [delay]);
}

export default useFroggerWrites;
